import { useEffect, useState } from "react";
import { USER_ROLES, type UserRole } from "@farnsworth/shared";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
} from "../components/ui.js";
import { useSession } from "../hooks/useSession.js";
import {
  useCreateUser,
  useDeleteUser,
  useResetUserPassword,
  useUpdateUser,
  useUsers,
} from "../hooks/useUsers.js";
import { formatDate } from "../lib/format.js";
import type { AdminUser } from "../types.js";

const MIN_PASSWORD_LENGTH = 8;
const PAGE_SIZE = 50;

/** Debounce keystrokes so typing "rob" does not fire three searches. */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function roleBadgeTone(role: UserRole): "indigo" | "amber" | "neutral" {
  if (role === "admin") return "indigo";
  if (role === "manager") return "amber";
  return "neutral";
}

/** Admin-only screen for creating and maintaining accounts. */
export function UsersPage() {
  const session = useSession();
  const me = session.data;
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const [showCreate, setShowCreate] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState<AdminUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [roleTarget, setRoleTarget] = useState<
    { user: AdminUser; nextRole: UserRole } | null
  >(null);

  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const debouncedSearch = useDebounced(search, 300);

  const usersQuery = useUsers({ q: debouncedSearch, limit: PAGE_SIZE, offset });

  // A new search starts at the first page; otherwise you land past the end.
  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch]);

  const users = usersQuery.data?.data ?? [];
  const meta = usersQuery.data?.meta;
  const total = meta?.total ?? 0;
  const pageStart = total === 0 ? 0 : (meta?.offset ?? offset) + 1;
  const pageEnd = Math.min((meta?.offset ?? offset) + (meta?.limit ?? PAGE_SIZE), total);
  const canGoBack = (meta?.offset ?? offset) > 0;
  const canGoForward = pageEnd < total;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-100">Users</h1>
          <p className="text-sm text-slate-400">
            Create accounts, set roles, reset passwords and disable access.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search email or username…"
            aria-label="Search users"
            className="w-full sm:w-64"
          />
          <Button onClick={() => setShowCreate(true)}>New user</Button>
        </div>
      </div>

      {usersQuery.isError ? <ErrorBanner message={(usersQuery.error as Error).message} /> : null}
      {updateUser.isError ? <ErrorBanner message={(updateUser.error as Error).message} /> : null}
      {deleteUser.isError ? <ErrorBanner message={(deleteUser.error as Error).message} /> : null}

      {usersQuery.isLoading ? (
        <Spinner label="Loading users…" />
      ) : users.length === 0 ? (
        debouncedSearch ? (
          <EmptyState
            title="No matches"
            hint={`No user matches “${debouncedSearch}”.`}
          />
        ) : (
          <EmptyState title="No users yet" hint="Create the first account to get started." />
        )
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="border-b border-slate-800 text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isSelf = user.id === me?.id;
                const pending = updateUser.isPending && updateUser.variables?.id === user.id;
                return (
                  <tr key={user.id} className="border-b border-slate-800/60 last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-100">
                        {user.username}
                        {isSelf ? <span className="ml-2 text-xs text-slate-400">(you)</span> : null}
                      </div>
                      <div className="text-xs text-slate-400">{user.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        aria-label={`Role for ${user.username}`}
                        value={user.role}
                        disabled={isSelf || pending}
                        onChange={(event) => {
                          const nextRole = event.target.value as UserRole;
                          if (nextRole === user.role) return;
                          setRoleTarget({ user, nextRole });
                        }}
                        className="w-32"
                      >
                        {USER_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      {user.isActive ? (
                        <Badge tone="green">Active</Badge>
                      ) : (
                        <Badge tone="red">Deactivated</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400">{formatDate(user.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <Button
                          variant="secondary"
                          onClick={() => {
                            updateUser.mutate({
                              id: user.id,
                              patch: { isActive: !user.isActive },
                            });
                          }}
                          disabled={isSelf || pending}
                        >
                          {user.isActive ? "Deactivate" : "Reactivate"}
                        </Button>
                        <Button variant="secondary" onClick={() => setPasswordTarget(user)}>
                          Reset password
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => setDeleteTarget(user)}
                          disabled={isSelf}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {users.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-400">
          <span>
            {total} user{total === 1 ? "" : "s"}
            {total > 0 ? ` · showing ${pageStart}–${pageEnd}` : ""}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={!canGoBack || usersQuery.isFetching}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={!canGoForward || usersQuery.isFetching}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      {showCreate ? <CreateUserModal onClose={() => setShowCreate(false)} /> : null}

      {passwordTarget ? (
        <ResetPasswordModal user={passwordTarget} onClose={() => setPasswordTarget(null)} />
      ) : null}

      {deleteTarget ? (
        <DeleteUserModal user={deleteTarget} onClose={() => setDeleteTarget(null)} />
      ) : null}

      {roleTarget ? (
        <ConfirmRoleModal
          user={roleTarget.user}
          nextRole={roleTarget.nextRole}
          onClose={() => setRoleTarget(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Role changes are not reversible by accident: demoting the wrong admin
 * locks someone out of the screens they maintain, so this asks first.
 * The select itself stays controlled by `user.role`, so cancelling simply
 * leaves it where it was.
 */
function ConfirmRoleModal({
  user,
  nextRole,
  onClose,
}: {
  user: AdminUser;
  nextRole: UserRole;
  onClose: () => void;
}) {
  const updateUser = useUpdateUser();
  const promoting = nextRole === "admin";

  return (
    <Modal title="Change role?" onClose={onClose}>
      <div className="space-y-4">
        {updateUser.isError ? (
          <ErrorBanner message={(updateUser.error as Error).message} />
        ) : null}
        <p className="text-sm text-slate-300">
          Change <span className="font-medium">{user.username}</span> from{" "}
          <span className="font-medium">{user.role}</span> to{" "}
          <span className="font-medium">{nextRole}</span>
          {promoting ? (
            <>
              . Admins can manage users, roles and all inventory — make sure this
              is intended.
            </>
          ) : (
            <>
              . They immediately lose access to whatever this role granted.
            </>
          )}
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={updateUser.isPending}
            onClick={() =>
              updateUser.mutate(
                { id: user.id, patch: { role: nextRole } },
                { onSuccess: () => onClose() },
              )
            }
          >
            {updateUser.isPending ? "Saving…" : `Change to ${nextRole}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const createUser = useCreateUser();
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>("staff");

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  return (
    <Modal title="New user" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          createUser.mutate(
            { email, username, password, role },
            { onSuccess: () => onClose() },
          );
        }}
      >
        {createUser.isError ? <ErrorBanner message={(createUser.error as Error).message} /> : null}

        <Field label="Email">
          <Input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="off"
            required
          />
        </Field>
        <Field label="Username" hint="Lowercase letters, digits, dot, underscore or hyphen.">
          <Input
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoCapitalize="none"
            spellCheck={false}
            required
          />
        </Field>
        <Field
          label="Temporary password"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters. Share it with the user; they can change it after signing in.`}
        >
          <Input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            required
          />
        </Field>
        <Field label="Role">
          <Select value={role} onChange={(event) => setRole(event.target.value as UserRole)}>
            {USER_ROLES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </Field>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={createUser.isPending || tooShort}>
            {createUser.isPending ? "Creating…" : "Create user"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ResetPasswordModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const resetPassword = useResetUserPassword();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;

  return (
    <Modal title={`Reset password — ${user.username}`} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          resetPassword.mutate({ id: user.id, password }, { onSuccess: () => onClose() });
        }}
      >
        {resetPassword.isError ? (
          <ErrorBanner message={(resetPassword.error as Error).message} />
        ) : null}

        <p className="text-sm text-slate-400">
          The new password takes effect immediately and signs the user out of every device.
        </p>

        <Field label="New password" hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}>
          <Input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            required
          />
        </Field>
        <Field label="Confirm password">
          <Input
            type="password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
            required
          />
        </Field>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={resetPassword.isPending || tooShort || mismatch}>
            {resetPassword.isPending ? "Saving…" : "Reset password"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function DeleteUserModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const deleteUser = useDeleteUser();
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal title={`Delete ${user.username}?`} onClose={onClose}>
      <div className="space-y-4">
        {error ? <ErrorBanner message={error} /> : null}
        <p className="text-sm text-slate-300">
          This permanently removes <span className="font-medium">{user.email}</span>. Their session
          ends immediately. Audit history is kept. To keep the account but block sign-in, deactivate
          it instead.
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={deleteUser.isPending}
            onClick={() =>
              deleteUser.mutate(user.id, {
                onSuccess: () => onClose(),
                onError: (mutationError) => setError(mutationError.message),
              })
            }
          >
            {deleteUser.isPending ? "Deleting…" : "Delete user"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
